/**
 * reportLayoutTokens — W4 (covers & page-chrome, RC2 format unification).
 *
 * THE single source of truth for the report's PAGE GEOMETRY (margins,
 * column-gap, paper background default) and the PAGE-NUMBER FORMAT helpers,
 * shared by the three paginators so they can never drift:
 *
 *   1. Puppeteer `page.pdf({ margin, footerTemplate })`   — server.ts
 *   2. Paged.js `@page { margin }` + the on-screen deck    — index.css /
 *                                                            PaginatedReportView
 *   3. Word (DOCX) page fields                             — reportDocxExport.ts
 *
 * WHY THIS EXISTS
 * ---------------
 * Before W4 each surface hand-coded its own geometry — the puppeteer
 * `page.pdf()` margins, the Paged.js injected `@page { margin }`, and the
 * print `@page { margin }` Cmd-P mirror in index.css were three independent
 * literals (originally 20/24/18/18mm) that
 * had to be hand-synced (the cover min-height in index.css is hand-derived
 * from the puppeteer top+bottom margins). Likewise the page-number FORMAT
 * lived in three places: the puppeteer `footerTemplate` HTML, the Word
 * footer/section fields, and the TOC page-map projection. A change to one
 * silently diverged the others — the exact class of "fix in one medium,
 * regress another" defect this rebuild exists to kill.
 *
 * BYTE-STABILITY
 * --------------
 * The constants below are the CURRENT production values. Routing every
 * surface through them is a pure refactor — no rendered output changes for
 * the default (no-preset) report. `DEFAULT_PAGE_BG` is the current white
 * (#ffffff), so a report with no paper tint stamps the same colour it does
 * today (and the var simply resolves to white).
 *
 * UNITS — millimetres for print/PDF page margins (the medium's native
 * unit), with the `@page` Cmd-P mirror expressed identically. The on-screen
 * column-gap is in `rem` (the screen's native unit). These are DIFFERENT
 * media with DIFFERENT native units; the tokens keep each medium's value in
 * one place rather than forcing a single unit across all three.
 */
import type { ReportTheme } from '../types/reportTheme';
// RENDER-BOUNDARY EM-DASH SANITIZER (house style — en-dash only). The deck
// running-header / footer are PagedJS @margin-box CSS strings built here from the
// site name, OUTSIDE the report body that `prepareReportForRender` normalizes, so
// they apply the same string primitive directly. Leaf import (zero deps) — no cycle.
import { normalizeDashes } from './reportRendering/normalizeReportDashes';

/**
 * PDF / print page margins, in millimetres, top → right → bottom → left.
 *
 * These are the authoritative puppeteer `page.pdf()` margins. The Paged.js
 * injected `@page` rule and the cover min-height in index.css are derived
 * from these (top + bottom drive the printable cover height).
 *
 * Asymmetric vertical margins: the larger bottom margin (16mm) reserves
 * room for the running footer (page number); the top (14mm) clears the
 * running header.
 *
 * v1.3.4 PRINT-DENSITY (#22): reduced from 20/18/24/18mm to 14/14/16/14mm.
 * This reclaims ~8mm width and ~14mm height per A4 page — the single biggest
 * page-count win — while keeping a comfortable typographic frame and ample
 * room for the running footer band. The cover min-height (index.css), the
 * Paged.js injected @page (PaginatedReportView), the puppeteer page.pdf()
 * margins (server.ts) and the native-print reset (PrintReportView) all derive
 * from this token and MUST move in lockstep; the reportLayoutTokens.test.ts
 * gate asserts the index.css literal still equals REPORT_PAGE_MARGIN_CSS.
 */
export const REPORT_PAGE_MARGIN_MM = {
  top: 14,
  right: 14,
  bottom: 16,
  left: 14,
} as const;

/** Convenience: the margins as `"20mm"`-style strings for `page.pdf()`. */
export const REPORT_PAGE_MARGIN_PDF: Readonly<{
  top: string;
  right: string;
  bottom: string;
  left: string;
}> = {
  top: `${REPORT_PAGE_MARGIN_MM.top}mm`,
  right: `${REPORT_PAGE_MARGIN_MM.right}mm`,
  bottom: `${REPORT_PAGE_MARGIN_MM.bottom}mm`,
  left: `${REPORT_PAGE_MARGIN_MM.left}mm`,
};

/**
 * The CSS `@page { margin: … }` shorthand value (top right bottom left),
 * EXACTLY matching {@link REPORT_PAGE_MARGIN_MM}. Used by the Paged.js
 * injected stylesheet in PaginatedReportView so the on-screen A4 deck +
 * Cmd-P print path agree with the puppeteer PDF margins.
 *
 * NB: index.css cannot import a TS constant; the `@page` rules there carry
 * the literal `14mm 14mm 16mm 14mm` with a comment pointing back here. The
 * `reportLayoutTokens.test.ts` gate asserts the literal still equals this
 * value so the two can never silently diverge.
 */
export const REPORT_PAGE_MARGIN_CSS = `${REPORT_PAGE_MARGIN_MM.top}mm ${REPORT_PAGE_MARGIN_MM.right}mm ${REPORT_PAGE_MARGIN_MM.bottom}mm ${REPORT_PAGE_MARGIN_MM.left}mm`;

/**
 * FULL-BLEED COVER PULL-OUT (DEFECT-2). A hero-image cover must reach the
 * physical page edges, but Paged.js lays the cover INSIDE the `.pagedjs_area`
 * content box, which is inset by the `@page` margins above. To make the cover
 * bleed, we apply NEGATIVE margins equal to the page margins, pulling each edge
 * back out to the sheet edge — derived from the SAME margin token so the pull
 * can never drift from the inset.
 *
 * top → right → bottom → left, each the negative of {@link REPORT_PAGE_MARGIN_MM}.
 */
export const REPORT_COVER_BLEED_MARGIN_CSS = `-${REPORT_PAGE_MARGIN_MM.top}mm -${REPORT_PAGE_MARGIN_MM.right}mm -${REPORT_PAGE_MARGIN_MM.bottom}mm -${REPORT_PAGE_MARGIN_MM.left}mm`;

/**
 * The full bleed page WIDTH for a full-bleed cover = content width + left +
 * right page margins. Expressed as a `calc()` against the `--report-page-width`
 * var (set by SiteRagReportShell; A4 210mm default) so it tracks A4/Letter.
 * Since the cover's box already spans the content width, adding both horizontal
 * margins back gives the full sheet width.
 */
export const REPORT_COVER_BLEED_WIDTH_CSS = `calc(var(--report-page-width, 210mm))`;

/**
 * CSS px per mm at 96dpi (the browser/Chromium reference). 96 / 25.4.
 * Used to derive a pixel printable-column width from the mm page model.
 */
export const CSS_PX_PER_MM = 96 / 25.4;

/** A4 portrait sheet width in mm. */
export const REPORT_PAGE_WIDTH_MM = 210;

/**
 * RRQ CHARTS FIX — the PRINTABLE content-column width in CSS px on an A4
 * sheet = (210mm − left − right margins) × 96/25.4.
 *
 * With the v1.3.4 14/14/16/14 margins this is (210 − 28) × 3.7795 ≈ 688px.
 *
 * WHY THIS EXISTS: the server PDF / /print export loads the report at a
 * 1280px screen-media viewport (server.ts page.setViewport), renders the
 * LIVE Recharts SVG against that full width, then `page.pdf()` reflows to
 * the A4 print box (preferCSSPageSize) WITHOUT Recharts re-measuring — so
 * the chart geometry (scales, clip-path, series/bar/polygon vertices) is
 * computed for ~1240px and then squeezed, clipping the series out of the
 * narrow printed column. Pinning the export root + the chart container to
 * THIS width at screen-media load makes Recharts build its geometry at the
 * final print width, so nothing is clipped. (`ChartFrame` derives a
 * per-exhibit width from this; index.css caps the export root to it.)
 */
export const REPORT_PRINTABLE_WIDTH_PX = Math.round(
  (REPORT_PAGE_WIDTH_MM - REPORT_PAGE_MARGIN_MM.left - REPORT_PAGE_MARGIN_MM.right) *
    CSS_PX_PER_MM,
);

/** The printable-column width as a CSS px string (e.g. `"688px"`). */
export const REPORT_PRINTABLE_WIDTH_CSS = `${REPORT_PRINTABLE_WIDTH_PX}px`;

/**
 * Inter-column gap for the two-column academic presets, in `rem`. ONE
 * value feeding the on-screen island/flow boxes, the studio-preview-print
 * mirror, and the @media-print export column engine in index.css.
 *
 * index.css carries the literal `1.5rem`; the gate test asserts it equals
 * this token so screen / print / studio-preview can't drift.
 */
export const REPORT_COLUMN_GAP_REM = 1.5;

/** The column-gap as a CSS length string (`"1.5rem"`). */
export const REPORT_COLUMN_GAP_CSS = `${REPORT_COLUMN_GAP_REM}rem`;

/**
 * RC2 — the DEFAULT page background (paper tint). This is the value the
 * `--page-bg` CSS variable resolves to when a report carries no preset /
 * no `backgroundColor` override, so a default report's frame + Paged.js
 * page surface + PDF stay byte-identical white.
 *
 * Paper tints (journal #fbfaf6, monograph #fcfcfb, review #fcfbf8) come
 * from `presentation.backgroundColor`; the renderers stamp that onto
 * `--page-bg` and `.pagedjs_page` reads it so the tint reaches the PDF /
 * print path, not just screen.
 */
export const DEFAULT_PAGE_BG = '#ffffff';

/** The CSS custom-property name carrying the resolved paper tint. */
export const PAGE_BG_VAR = '--page-bg';

/**
 * Relative-luminance threshold below which a colour is treated as "not
 * paper" — i.e. too dark to ever be a report page background. A report is
 * ALWAYS a light-paper document (independent of the app's dark/light mode),
 * so a dark-luminance value resolving onto the page surface is by definition
 * the wrong colour (the app/store dark theme bleeding in — never an intended
 * paper tint). The light preset paper tints all sit at relative luminance
 * ≈ 0.97+ (journal #fbfaf6 ≈ 0.97, monograph #fcfcfb ≈ 0.98, review
 * #fcfbf8 ≈ 0.98, white #ffffff = 1.0), while every dark app surface
 * (#0b0f17, #0f172a, slate-900, …) sits below ≈ 0.05 — so a 0.5 cut cleanly
 * separates "legitimate paper" from "dark app theme bleeding through" with a
 * very wide margin on both sides.
 */
const PAPER_MIN_LUMINANCE = 0.5;

/**
 * Parse a `#rgb` / `#rrggbb` hex string into [r,g,b] (0–255), or `null`
 * when the value is not a parseable hex (named colours, `rgb()`, gradients,
 * etc.). Local to this module so the layout-token contract carries no extra
 * dependency.
 */
function parseHexRgb(value: string): [number, number, number] | null {
  const m = value.trim().replace(/^#/, '');
  const expanded = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) return null;
  return [
    parseInt(expanded.slice(0, 2), 16),
    parseInt(expanded.slice(2, 4), 16),
    parseInt(expanded.slice(4, 6), 16),
  ];
}

/**
 * WCAG relative luminance (0 = black … 1 = white) for an [r,g,b] triple.
 * Used only for the light/dark paper guard, so the sRGB linearisation is
 * the standard WCAG formula.
 */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * True when a colour is too dark to be a report PAGE background — i.e. it
 * parses as a hex below {@link PAPER_MIN_LUMINANCE}. Unparseable values
 * (named colours, `rgb()`, …) return `false`: we can't prove they are dark,
 * so we leave them verbatim rather than risk clobbering an exotic-but-light
 * tenant tint. The current presets are all hex, so this is conservative and
 * byte-stable for them.
 */
export function isDarkPaperColor(backgroundColor: string | null | undefined): boolean {
  const rgb = parseHexRgb((backgroundColor ?? '').trim());
  if (!rgb) return false;
  return relativeLuminance(rgb) < PAPER_MIN_LUMINANCE;
}

/**
 * Build the `--page-bg` value to stamp on the report frame. Returns the
 * resolved paper tint, or {@link DEFAULT_PAGE_BG} when none is supplied —
 * so an absent/blank background is byte-identical white.
 *
 * PAPER GUARD (W4 black-pages regression fix): a report is ALWAYS a
 * light-paper document, independent of the app's dark/light mode. In the
 * LIVE viewer the page background resolves off the store/app report theme
 * (via `resolveReportPresentation` → `resolveEffectiveTheme`); when that
 * theme carries a DARK `backgroundColor` (the app's dark theme bleeding in,
 * with no light-paper preset to override it) the page surface would go
 * black. So a dark-luminance value here is by definition NOT a paper tint —
 * it is the dark app theme leaking onto the page — and we substitute white
 * paper ({@link DEFAULT_PAGE_BG}). The legitimate light preset paper tints
 * (journal/monograph/review/white) sit far above the threshold and pass
 * through verbatim, so they are unaffected. The visual harness masked this
 * because `SampleReportPrintView` floors every theme on `DEFAULT_THEME`
 * (white) — it never feeds a dark store base — so this guard makes the
 * resolver correct for the live mount the harness never exercised.
 */
export function resolvePageBg(backgroundColor: string | null | undefined): string {
  const trimmed = (backgroundColor ?? '').trim();
  if (trimmed.length === 0) return DEFAULT_PAGE_BG;
  // A report page is never dark — a dark value is the app/store dark theme
  // bleeding in, not an intended paper tint. Fall back to white paper.
  if (isDarkPaperColor(trimmed)) return DEFAULT_PAGE_BG;
  return trimmed;
}

// ── PAGE-NUMBER FORMAT ───────────────────────────────────────────────
/**
 * The page-number format token, mirrored from
 * `ReportTheme['pageNumberFormat']` so every paginator reads the SAME
 * union:
 *   - 'arabic'        → "N"            (bare decimal)
 *   - 'roman-front'   → "i" / "ii"     (lowercase roman running number)
 *   - 'page-of-total' → "Page N of M"  (the historical default footer)
 */
export type ReportPageNumberFormat = ReportTheme['pageNumberFormat'];

/** Normalise an arbitrary value to a known page-number format token,
 *  defaulting to the historical `'page-of-total'` (byte-stable). */
export function normalizePageNumberFormat(
  value: string | null | undefined,
): ReportPageNumberFormat {
  return value === 'arabic' || value === 'roman-front' ? value : 'page-of-total';
}

/** Convert a positive integer to a lowercase roman numeral. Used for the
 *  `roman-front` format in the TOC + screen page-number surfaces (the DOCX
 *  path lets Word render roman via `NumberFormat.LOWER_ROMAN`). */
export function toLowerRoman(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return String(n);
  const num = Math.floor(n);
  const table: ReadonlyArray<readonly [number, string]> = [
    [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'],
    [100, 'c'], [90, 'xc'], [50, 'l'], [40, 'xl'],
    [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
  ];
  let remaining = num;
  let out = '';
  for (const [value, glyph] of table) {
    while (remaining >= value) {
      out += glyph;
      remaining -= value;
    }
  }
  return out;
}

/**
 * Format a SINGLE page number (e.g. a TOC target page, or a screen running
 * number) per the resolved format token. `page-of-total` and `arabic` both
 * render the bare decimal here — "Page N of M" is a FOOTER-only treatment
 * (it needs the total page count, which a TOC entry doesn't have). Only
 * `roman-front` changes the glyph.
 *
 * This keeps the TOC / screen surfaces format-faithful without needing the
 * document total, so the no-format (default) and `arabic` paths render the
 * exact same `String(page)` they do today (byte-stable TOC).
 */
export function formatPageNumber(
  page: number,
  format: ReportPageNumberFormat,
): string {
  if (format === 'roman-front') return toLowerRoman(page);
  return String(page);
}

/**
 * Build the running-footer page-number HTML for the puppeteer
 * `footerTemplate`. Puppeteer injects live `<span class="pageNumber">` /
 * `<span class="totalPages">` values at print time, so the format here is
 * expressed via those spans (we cannot pre-compute roman glyphs server-side
 * — puppeteer fills the spans after layout). The three formats map to:
 *   - 'page-of-total' → `Page <pageNumber> of <totalPages>`  (default)
 *   - 'arabic'        → `<pageNumber>`
 *   - 'roman-front'   → `<pageNumber>` (bare running number; matches the
 *                       DOCX `roman-front` footer, which also shows the bare
 *                       running number with the roman glyph supplied by the
 *                       section's number format)
 *
 * Returns ONLY the inner page-number markup — the caller wraps it in the
 * styled footer `<div>` (so per-endpoint label prefixes are preserved).
 */
export function buildPuppeteerFooterPageNumber(format: ReportPageNumberFormat): string {
  if (format === 'page-of-total') {
    return 'Page <span class="pageNumber"></span> of <span class="totalPages"></span>';
  }
  return '<span class="pageNumber"></span>';
}

/**
 * RRQ DEPLOY-BLOCKER FIX (6a) — FOOTER PARITY between the on-screen Paged.js
 * deck and the server puppeteer PDF.
 *
 * The puppeteer PDF draws `Datacrate — {site} — Page N of M` via
 * {@link buildPuppeteerFooterPageNumber}; the deck previously drew ONLY
 * `counter(page) " / " counter(pages)` (a bare `N / M`, no brand/site) in its
 * `@page { @bottom-center { content: … } }` margin box. So viewer and PDF
 * footers diverged. This builder produces the deck `@bottom-center` `content`
 * VALUE that mirrors the puppeteer footer EXACTLY — the same brand prefix, the
 * same site segment, and the same page-number FORMAT — but using CSS Paged-Media
 * counters (`counter(page)` / `counter(pages)`) for the live running numbers
 * the deck paints. ONE source so the two surfaces can never drift.
 *
 * Returns a CSS `content` value (a string of quoted literals + counter()
 * tokens) ready to drop straight into `@bottom-center { content: <here>; }`.
 * The site segment is omitted when no site name is supplied (mirrors the
 * server's `safeSiteName` fallback, which always supplies one for the PDF).
 */
export function buildDeckFooterContent(
  format: ReportPageNumberFormat,
  siteName?: string | null,
): string {
  // Escape any embedded double-quote so the CSS string literal stays valid, and
  // collapse em-dash → en-dash so the running FOOTER matches the house style the
  // report body (`prepareReportForRender`) and the DOCX/PPTX exports enforce.
  const safeSite = normalizeDashes((siteName ?? '').trim()).replace(/["\\]/g, '');
  const brand = `"Datacrate "`;
  const sitePart = safeSite ? `"· ${safeSite} · "` : `"· "`;
  const pagePart =
    format === 'page-of-total'
      ? `"Page " counter(page) " of " counter(pages)`
      : `counter(page)`;
  return `${brand} ${sitePart} ${pagePart}`;
}

/**
 * GC-W5 — the `@top-right` RUNNING-HEADER `content` value for the paged deck:
 * "<SITE> · <TYPE>" (e.g. "Northwind Industrial Park · BTM Feasibility"). The
 * golden's running header sits top-right on every content page (suppressed on
 * the cover by the caller's `@page :first`). Mirrors {@link buildDeckFooterContent}
 * — one source, CSS string literals, the same `safeSite` escaping. Returns ''
 * when no site is supplied so the caller omits the margin box entirely. The cell
 * is uppercased via CSS `text-transform`, so the label is passed verbatim here.
 */
export function buildDeckHeaderContent(
  siteName?: string | null,
  typeLabel?: string | null,
): string {
  // Collapse em-dash → en-dash (house style) before escaping, so the running
  // HEADER "<SITE> · <TYPE>" never paints a U+2014 the report body strips.
  const safeSite = normalizeDashes((siteName ?? '').trim()).replace(/["\\]/g, '');
  if (!safeSite) return '';
  const safeType = (typeLabel ?? '').trim().replace(/["\\]/g, '');
  return safeType ? `"${safeSite} · ${safeType}"` : `"${safeSite}"`;
}
